/**
 * Deep-dive report renderer (SA-237) — per-conversation root-cause cards,
 * cross-patterns, and three-way recommendations (incl. operations). Section
 * order mirrors render_deep_dive_markdown; a report that fails
 * reportHasDeepDiveShape falls back to report_markdown (AnalysisRunPage).
 */
import { Link } from 'react-router-dom'

import { Badge } from '@/components/ui'
import type {
  AnalysisReport,
  AnalysisRunDetail,
  DeepDiveConversationEntry,
} from '@/types/conversationAnalysis'

export function reportHasDeepDiveShape(
  report: AnalysisReport | null,
): report is AnalysisReport {
  return (
    !!report &&
    typeof report.executive_summary === 'string' &&
    (report.per_conversation === undefined ||
      Array.isArray(report.per_conversation))
  )
}

const ENTRY_SECTIONS: [string, keyof DeepDiveConversationEntry][] = [
  ['Breakdown point', 'breakdown_point'],
  ['What happened', 'what_happened'],
  ['What should have happened', 'what_should_have_happened'],
]

function conversationLink(id: string, inboxEnabled: boolean): string {
  return inboxEnabled ? `/inbox/${id}` : `/conversations/${id}`
}

export function DeepDiveSections({
  run,
  inboxEnabled,
}: {
  run: AnalysisRunDetail
  inboxEnabled: boolean
}) {
  const report = run.report ?? {}
  const entries = Array.isArray(report.per_conversation)
    ? report.per_conversation
    : []
  const patterns = Array.isArray(report.cross_patterns)
    ? report.cross_patterns
    : []
  const recommendations = report.recommendations ?? {}
  const recSections: [string, string[]][] = [
    ['Prompt', recommendations.prompt ?? []],
    ['Workflow config', recommendations.workflow_config ?? []],
    ['Operations', recommendations.operations ?? []],
  ]

  return (
    <div className="space-y-6">
      {typeof report.executive_summary === 'string' &&
        report.executive_summary.trim() !== '' && (
          <section className="space-y-2 print:break-inside-avoid">
            <h2 className="text-sm font-semibold text-foreground">
              Executive summary
            </h2>
            <p className="text-sm text-foreground whitespace-pre-wrap">
              {report.executive_summary}
            </p>
          </section>
        )}

      {entries.map((entry) => (
        <section
          key={entry.conversation_id}
          className="rounded-sm border border-border bg-card p-3 space-y-2 print:break-inside-avoid"
        >
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-foreground">
              Conversation{' '}
              <Link
                to={conversationLink(entry.conversation_id, inboxEnabled)}
                className="text-primary hover:underline font-mono text-xs"
              >
                {entry.conversation_id}
              </Link>
            </h2>
            {entry.breakdown_stage?.trim() && (
              <Badge variant="outline">
                {entry.breakdown_stage.replace(/_/g, ' ')}
              </Badge>
            )}
          </div>
          {ENTRY_SECTIONS.map(([title, key]) => {
            const body = (entry[key] ?? '').trim()
            if (!body) return null
            return (
              <p key={key} className="text-sm text-foreground">
                <span className="font-medium">{title}:</span>{' '}
                <span className="whitespace-pre-wrap">{body}</span>
              </p>
            )
          })}
        </section>
      ))}

      {patterns.length > 0 && (
        <section className="space-y-2 print:break-inside-avoid">
          <h2 className="text-sm font-semibold text-foreground">
            Cross-conversation patterns
          </h2>
          <ul className="space-y-1">
            {patterns.map((pattern, i) => (
              <li key={i} className="text-sm text-foreground">
                {pattern.pattern}
                {pattern.conversation_ids.length > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {' — '}
                    {pattern.conversation_ids.map((id, j) => (
                      <span key={id}>
                        {j > 0 && ', '}
                        <Link
                          to={conversationLink(id, inboxEnabled)}
                          className="text-primary hover:underline"
                        >
                          {id}
                        </Link>
                      </span>
                    ))}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {recSections.some(([, items]) => items.length > 0) && (
        <section className="space-y-2 print:break-inside-avoid">
          <h2 className="text-sm font-semibold text-foreground">
            Recommendations
          </h2>
          <div className="grid gap-4 sm:grid-cols-3">
            {recSections.map(([label, items]) =>
              items.length > 0 ? (
                <div key={label}>
                  <h3 className="text-xs font-medium text-muted-foreground mb-1">
                    {label}
                  </h3>
                  <ul className="list-disc pl-5 text-sm text-foreground space-y-1">
                    {items.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              ) : null,
            )}
          </div>
        </section>
      )}
    </div>
  )
}
